import { CartAddEvent } from '@theme/events';

class PackSelection extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.drawerContent = null;
    this.message = null;
    this.pageMessage = null;
    this.slots = null;
    this.addAllButton = null;
    this.packId = '';
    this.packSize = 0;
    this.selectedItems = [];
    this.isAdding = false;
  }

  connectedCallback() {
    this.drawer = this.querySelector('[data-pack-drawer]');
    this.drawerContent = this.querySelector('[data-pack-drawer-content]');
    this.message = this.querySelector('[data-pack-message]');
    this.pageMessage = this.querySelector('[data-pack-page-message]');
    this.slots = this.querySelector('[data-pack-slots]');
    this.addAllButton = this.querySelector('[data-pack-add-all]');
    this.packId = this.createPackId();

    if (this.addAllButton) {
      this.addAllLabel = this.addAllButton.textContent.trim() || 'Add all to cart';
    }

    this.buildPackOptions();
    this.bindEvents();
    this.updateProgress();
  }

  buildPackOptions() {
    const host = this.querySelector('[data-pack-size-options]');
    if (!host) return;

    let options = [];
    if (this.dataset.packOptions) {
      try {
        options = JSON.parse(this.dataset.packOptions);
      } catch (e) {
        console.error('Error parsing pack options JSON:', e);
      }
    }

    if (!options || !options.length) {
      const fallbackMax = Number(this.dataset.maxItems) || 32;
      const defaultSizes = [2, 4, 6, 12, 24, 32];
      options = defaultSizes.map((size) => ({ size, active: true }));
    }

    // Find the first active option to check it by default
    const firstActiveOption = options.find((opt) => opt.active);
    const defaultCheckOption = firstActiveOption || null;

    const perJarMap = {
      2: '14.99',
      4: '14.74',
      6: '13.83',
      12: '13.33',
      24: '12.92',
      32: '12.50',
    };

    if (this.dataset.perJarPrices) {
      try {
        const parsed = JSON.parse(this.dataset.perJarPrices);
        if (parsed && typeof parsed === 'object') {
          Object.assign(perJarMap, parsed);
        }
      } catch (e) {
        console.error('Error parsing perJarPrices dataset:', e);
      }
    }

    host.innerHTML = options
      .map((opt) => {
        const checked = defaultCheckOption && opt.size === defaultCheckOption.size ? 'checked' : '';
        const disabled = opt.active ? '' : 'disabled';
        
        let perJarVal = perJarMap[opt.size];
        if (!perJarVal && opt.size) {
          const packPriceCents = this.calculatePackPrice(opt.size);
          if (packPriceCents) {
            perJarVal = (packPriceCents / (opt.size * 100)).toFixed(2);
          }
        } else if (perJarVal) {
          perJarVal = Number(perJarVal).toFixed(2);
        }

        const perJar = perJarVal
          ? `<span class="pack-option__per-jar">$${perJarVal}/jar</span>`
          : '';
        return `
          <label class="pack-selection__pack-option">
            <input type="radio" name="pack-size-${this.packId}" value="${opt.size}" data-active="${opt.active}" ${checked} ${disabled}>
            <span><strong>${opt.size}-Pack</strong>${perJar}</span>
          </label>
        `;
      })
      .join('');

    this.packSize = defaultCheckOption ? defaultCheckOption.size : 0;
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const cardAddButton = target.closest('[data-pack-add-card]');
      if (cardAddButton instanceof HTMLButtonElement) {
        const item = cardAddButton.closest('[data-product-id]');
        if (item instanceof HTMLElement) this.stageProductFromCard(item);
        return;
      }

      const removeButton = target.closest('[data-pack-remove-index]');
      if (removeButton instanceof HTMLButtonElement) {
        this.removeStagedItem(Number(removeButton.dataset.packRemoveIndex));
        return;
      }

      if (target.closest('[data-pack-add-all]')) {
        this.addAllToCart();
        return;
      }

      const openButton = target.closest('[data-pack-open]');
      if (openButton) {
        const item = openButton.closest('[data-product-id]');
        if (item instanceof HTMLElement) this.openProduct(item);
      }

      if (target.closest('[data-pack-close]')) this.closeDrawer();

      const scrollToSummaryBtn = target.closest('[data-pack-scroll-to-summary]');
      if (scrollToSummaryBtn) {
        event.preventDefault();
        const summaryEl = this.querySelector('.pack-selection__summary');
        if (summaryEl) {
          const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          const targetY = summaryEl.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({
            top: targetY,
            behavior: 'smooth'
          });
        }
        return;
      }
    });

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== `pack-size-${this.packId}`) return;

      const nextSize = Number(target.value);
      if (nextSize < this.selectedItems.length) {
        this.setMessage(`Remove ${this.selectedItems.length - nextSize} item(s) before switching to ${nextSize}-pack.`);
        this.restoreCheckedPackSize();
        return;
      }

      this.packSize = nextSize;
      this.setMessage('');
      this.updateProgress();
    });
  }

  openProduct(item) {
    const template = item.querySelector('[data-pack-product-details]');
    if (!(template instanceof HTMLTemplateElement) || !this.drawer || !this.drawerContent) return;

    this.drawerContent.replaceChildren(template.content.cloneNode(true));
    this.drawer.hidden = false;
    document.documentElement.classList.add('pack-drawer-open');

    const title = this.drawerContent.querySelector('.pack-drawer__title');
    if (title) title.id = 'PackDrawerTitle';
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = true;
    document.documentElement.classList.remove('pack-drawer-open');
  }

  stageProductFromCard(item) {
    if (this.selectedItems.length >= this.packSize) {
      this.setMessage(`Your ${this.packSize}-pack is full.`);
      this.updateAddState();
      return;
    }

    const product = this.getProductFromItem(item);
    const selectedVariant = product?.variants?.find((variant) => variant.available) || product?.variants?.[0];

    if (!product || !selectedVariant?.available) {
      this.setMessage('This item is not available.');
      return;
    }

    this.selectedItems.push({
      productId: product.id,
      variantId: selectedVariant.id,
      title: product.title,
    });

    this.setMessage(this.selectedItems.length >= this.packSize ? `Your ${this.packSize}-pack is ready.` : 'Added to your pack.');
    this.updateProgress();
  }

  removeStagedItem(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.selectedItems.length) return;

    this.selectedItems.splice(index, 1);
    this.setMessage('Removed from your pack.');
    this.updateProgress();
  }

  showUpsellModal() {
    const badge = this.dataset.upsellBadge || 'Recommended Upgrade';
    const heading = this.dataset.upsellHeading || 'Double Your Cake Jars & Save on Shipping!';
    const nudge = this.dataset.upsellNudge || 'For just <strong>$19.01</strong> more, get <strong>2 extra jars</strong> and <strong>FREE shipping</strong>!';
    const note = this.dataset.upsellNote || "Don't worry, your extra jars can be frozen to enjoy later!";
    const upgradeLabel = this.dataset.upsellUpgradeLabel || 'Upgrade to 4 Pack & Get Free Shipping';
    const keepLabel = this.dataset.upsellKeepLabel || 'No thanks, keep 2 Pack (+$9.99 shipping)';
    const triggerSize = Number(this.dataset.upsellTrigger) || 2;
    const targetSize = Number(this.dataset.upsellTarget) || 4;

    return new Promise((resolve) => {
      let modal = document.querySelector('[data-pack-upsell-modal]');
      if (!modal) {
        modal = document.createElement('div');
        modal.className = 'pack-upsell-modal';
        modal.setAttribute('data-pack-upsell-modal', '');
        document.body.appendChild(modal);
      }

      const triggerPrice = (this.calculatePackPrice(triggerSize) / 100).toFixed(2);
      const targetPrice = (this.calculatePackPrice(targetSize) / 100).toFixed(2);
      const triggerShipping = triggerSize === 2 ? '+ $9.99 Shipping' : 'Flat Rate Shipping';
      const triggerTotal = triggerSize === 2 ? (Number(triggerPrice) + 9.99).toFixed(2) : triggerPrice;

      modal.innerHTML = `
        <div class="pack-upsell-modal__backdrop"></div>
        <div class="pack-upsell-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="PackUpsellTitle">
          <div class="pack-upsell-modal__header">
            <span class="pack-upsell-modal__badge">${this.escapeHtml(badge)}</span>
            <h2 id="PackUpsellTitle" class="pack-upsell-modal__title">${this.escapeHtml(heading)}</h2>
          </div>
          <div class="pack-upsell-modal__body">
            <div class="pack-upsell-modal__comparison">
              <div class="pack-upsell-modal__card pack-upsell-modal__card--current">
                <span class="pack-upsell-modal__card-label">Current Choice</span>
                <div class="pack-upsell-modal__card-title">${triggerSize} Pack</div>
                <div class="pack-upsell-modal__card-price">$${triggerPrice}</div>
                <div class="pack-upsell-modal__card-shipping">${triggerShipping}</div>
                <div class="pack-upsell-modal__card-total">Total: $${triggerTotal}</div>
              </div>
              <div class="pack-upsell-modal__card pack-upsell-modal__card--upgrade">
                <span class="pack-upsell-modal__card-label">Best Value</span>
                <div class="pack-upsell-modal__card-title">${targetSize} Pack</div>
                <div class="pack-upsell-modal__card-price">$${targetPrice}</div>
                <div class="pack-upsell-modal__card-shipping pack-upsell-modal__free-shipping">FREE Shipping</div>
                <div class="pack-upsell-modal__card-total">Total: $${targetPrice}</div>
              </div>
            </div>
            <div class="pack-upsell-modal__nudge">
              ${nudge}
            </div>
            ${note ? `<div class="pack-upsell-modal__note">${this.escapeHtml(note)}</div>` : ''}
          </div>
          <div class="pack-upsell-modal__footer">
            <button type="button" class="button pack-upsell-modal__btn-upgrade" data-upsell-action="upgrade">
              ${this.escapeHtml(upgradeLabel)}
            </button>
            <button type="button" class="button button--secondary pack-upsell-modal__btn-keep" data-upsell-action="keep">
              ${this.escapeHtml(keepLabel)}
            </button>
          </div>
        </div>
      `;

      modal.hidden = false;
      document.body.classList.add('pack-upsell-open');

      const cleanup = (choice) => {
        modal.hidden = true;
        document.body.classList.remove('pack-upsell-open');
        resolve(choice);
      };

      const handleBtnClick = (evt) => {
        const target = evt.target;
        if (!(target instanceof HTMLElement)) return;
        const btn = target.closest('[data-upsell-action]');
        if (!btn) return;
        const action = btn.dataset.upsellAction;
        modal.removeEventListener('click', handleBtnClick);
        cleanup(action);
      };

      modal.addEventListener('click', handleBtnClick);
    });
  }

  async addAllToCart() {
    if (this.isAdding) return;

    if (this.selectedItems.length !== this.packSize) {
      const remaining = this.packSize - this.selectedItems.length;
      this.setMessage(`Add ${remaining} more item(s) to complete your ${this.packSize}-pack.`);
      return;
    }

    let finalPackSize = this.packSize;
    let finalItems = [...this.selectedItems];

    const triggerSize = Number(this.dataset.upsellTrigger) || 2;
    const targetSize = Number(this.dataset.upsellTarget) || 4;
    const enableUpsell = this.dataset.enableUpsell !== 'false';

    if (enableUpsell && this.packSize === triggerSize) {
      const choice = await this.showUpsellModal();
      if (choice === 'upgrade') {
        this.packSize = targetSize;
        this.restoreCheckedPackSize();
        const diff = targetSize - triggerSize;
        this.setMessage(`Upgraded to ${targetSize}-Pack! Select ${diff} more flavor${diff > 1 ? 's' : ''} to complete your pack.`);
        this.updateProgress();
        return;
      }
    }

    this.isAdding = true;
    this.setMessage('Adding pack to cart...');
    this.updateAddState();

    await this.submitBundleToCart(finalPackSize, finalItems);
  }

  async submitBundleToCart(packSize, selectedItems) {
    const sections = this.getCartSectionIds();
    let bundleVariants = {};
    if (this.dataset.bundleVariants) {
      try {
        bundleVariants = JSON.parse(this.dataset.bundleVariants);
      } catch (e) {
        console.error('Error parsing bundle variants:', e);
      }
    }

    const perJarPrices = {
      2: '14.99',
      4: '14.74',
      6: '13.83',
      12: '13.33',
      24: '12.92',
      32: '12.50',
    };

    const perJarRaw = perJarPrices[packSize] || (this.calculatePackPrice(packSize) / (packSize * 100)).toFixed(2);
    const perJar = Number(perJarRaw).toFixed(2);
    const bundleInfo = bundleVariants[packSize];

    const flavorCounts = {};
    selectedItems.forEach((item) => {
      flavorCounts[item.title] = (flavorCounts[item.title] || 0) + 1;
    });

    const flavorSummary = Object.entries(flavorCounts)
      .map(([name, count]) => (count > 1 ? `${name} (x${count})` : name))
      .join(', ');

    const properties = {
      'Pack': `${packSize}-Pack`,
      'Flavors': flavorSummary,
      'Per Jar Price': `$${perJar}`,
      '_Pack Feature': 'Pack Selection',
      '_Pack ID': this.packId,
      '_Pack Size': `${packSize}-pack`,
      '_Pack Jar Type': this.dataset.jarType || '',
      '_Pack Pricing Strategy': 'fixed_bundle',
      '_Pack Fixed Bundle Price': (this.calculatePackPrice(packSize) / 100).toFixed(2),
      '_Pack Per Jar Price': perJar,
    };

    selectedItems.forEach((item, index) => {
      properties[`Jar ${index + 1}`] = item.title;
      properties[`_Flavor ${index + 1}`] = item.title;
    });

    let itemsToAdd = [];

    if (bundleInfo && bundleInfo.variantId) {
      itemsToAdd.push({
        id: Number(bundleInfo.variantId),
        quantity: 1,
        properties: properties,
      });
    } else {
      itemsToAdd = selectedItems.map((item, index) => ({
        id: Number(item.variantId),
        quantity: 1,
        properties: {
          ...properties,
          '_Pack Line ID': `${this.packId}-${index}`,
        },
      }));
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: itemsToAdd,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await response.json();

      if (added.status) {
        this.setMessage(added.message || 'Unable to add this pack.');
        return;
      }

      const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());
      document.dispatchEvent(
        new CartAddEvent(cart, 'pack-selection', {
          source: 'pack-selection',
          itemCount: selectedItems.length,
          sections: added.sections,
        })
      );

      this.selectedItems = [];
      this.packId = this.createPackId();
      this.buildPackOptions();
      this.setMessage('Pack added to cart.');
      this.openThemeCartDrawer();
      setTimeout(() => {
        if (this.selectedItems.length === 0) {
          this.setMessage('');
        }
      }, 3000);
    } catch (error) {
      console.error(error);
      this.setMessage('Unable to add this pack. Please try again.');
    } finally {
      this.isAdding = false;
      this.updateProgress();
    }
  }

  getProductFromItem(item) {
    const json = item.querySelector('[data-pack-product-json]');
    if (!json?.textContent) return null;
    return JSON.parse(json.textContent);
  }

  updateProgress() {
    this.querySelectorAll('[data-pack-count]').forEach((element) => {
      element.textContent = String(this.selectedItems.length);
    });
    this.querySelectorAll('[data-pack-limit]').forEach((element) => {
      element.textContent = String(this.packSize);
    });
    this.renderSlots();
    this.updatePackOptionState();
    this.updateCardBadges();
    this.updateStickyBar();
    this.updateAddState();
  }

  updateCardBadges() {
    const counts = {};
    this.selectedItems.forEach((item) => {
      counts[item.productId] = (counts[item.productId] || 0) + 1;
    });

    this.querySelectorAll('[data-product-id]').forEach((card) => {
      const productId = Number(card.dataset.productId);
      const badge = card.querySelector('[data-pack-badge]');
      if (!badge) return;

      const count = counts[productId] || 0;
      if (count > 0) {
        badge.textContent = String(count);
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  updateStickyBar() {
    const helpEl = this.querySelector('[data-sticky-pack-help]');
    if (!helpEl) return;

    const remaining = this.packSize - this.selectedItems.length;
    if (remaining > 0) {
      helpEl.textContent = `You have ${remaining} jar${remaining > 1 ? 's' : ''} left to add`;
    } else {
      helpEl.textContent = `Your ${this.packSize}-Pack is complete!`;
    }
  }

  renderSlots() {
    if (!this.slots) return;

    const slots = this.selectedItems.map((item, index) => {
      return `
        <div class="pack-selection__slot is-filled">
          <span class="pack-selection__slot-title">${this.escapeHtml(item.title || 'Selected')}</span>
          <button
            class="pack-selection__slot-remove"
            type="button"
            data-pack-remove-index="${index}"
            aria-label="Remove ${this.escapeHtml(item.title || 'item')} from pack"
          >
            &times;
          </button>
        </div>
      `;
    });

    for (let index = this.selectedItems.length; index < this.packSize; index += 1) {
      slots.push('<div class="pack-selection__slot"><span class="pack-selection__slot-title">Select Flavor</span></div>');
    }

    this.slots.innerHTML = slots.join('');
  }

  updateAddState() {
    const isFull = this.packSize === 0 || this.selectedItems.length >= this.packSize;

    this.querySelectorAll('[data-pack-add-card]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = isFull || this.isAdding;
      button.classList.toggle('is-disabled-by-pack', isFull);
    });

    this.querySelectorAll('[data-pack-add-all]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = this.packSize === 0 || this.selectedItems.length !== this.packSize || this.isAdding;
      if (this.packSize > 0) {
        const priceCents = this.calculatePackPrice(this.packSize);
        const formattedPrice = this.formatPrice(priceCents);
        button.textContent = `${this.addAllLabel} — ${formattedPrice}`;
      } else {
        button.textContent = this.addAllLabel;
      }
    });
  }

  updatePackOptionState() {
    this.querySelectorAll(`input[name="pack-size-${this.packId}"]`).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const isConfiguredActive = input.dataset.active === 'true';
      input.disabled = !isConfiguredActive || Number(input.value) < this.selectedItems.length || this.isAdding;
      input.checked = Number(input.value) === this.packSize;
    });
  }

  restoreCheckedPackSize() {
    this.querySelectorAll(`input[name="pack-size-${this.packId}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = Number(input.value) === this.packSize;
    });
  }

  rebuildPackOptionNames() {
    this.querySelectorAll('.pack-selection__pack-option input').forEach((input) => {
      if (input instanceof HTMLInputElement) input.name = `pack-size-${this.packId}`;
    });
    this.restoreCheckedPackSize();
  }

  setMessage(text) {
    if (this.message) this.message.textContent = text;
    if (this.pageMessage) this.pageMessage.textContent = text;
  }

  createPackId() {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) ids.push(component.dataset.sectionId);
    });
    return [...new Set(ids)];
  }

  openThemeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
  }

  formatPrice(cents) {
    const amount = (cents / 100).toFixed(2);
    let symbol = '$';
    const subtotalEl = document.querySelector('.cart__total-value, .totals__total-value, .cart__subtotal-value');
    if (subtotalEl) {
      const text = subtotalEl.textContent || '';
      if (text.includes('£')) symbol = '£';
      else if (text.includes('€')) symbol = '€';
      else if (text.includes('CA$')) symbol = 'CA$';
      else if (text.includes('A$')) symbol = 'A$';
    }
    return `${symbol}${amount}`;
  }

  calculatePackPrice(size) {
    const strategy = this.dataset.pricingStrategy || 'product_page';
    let basePriceCents = 1474;

    const firstProductJsonEl = this.querySelector('[data-pack-product-json]');
    if (firstProductJsonEl && firstProductJsonEl.textContent) {
      try {
        const prod = JSON.parse(firstProductJsonEl.textContent);
        if (prod && prod.price) {
          basePriceCents = prod.price;
        }
      } catch (e) {
        console.error('Error parsing first product JSON for price:', e);
      }
    }

    if (strategy === 'fixed_bundle') {
      const fixedPriceStr = this.getTierValue(this.dataset.fixedPriceTiers, size);
      if (fixedPriceStr) {
        const priceNum = parseFloat(fixedPriceStr);
        if (!isNaN(priceNum)) {
          return Math.round(priceNum * 100);
        }
      }
    } else if (strategy === 'percentage_discount') {
      const discountStr = this.getTierValue(this.dataset.discountTiers, size);
      if (discountStr) {
        const discountPercent = parseFloat(discountStr);
        if (!isNaN(discountPercent) && discountPercent > 0) {
          const originalPrice = basePriceCents * size;
          return Math.round(originalPrice * (1 - discountPercent / 100));
        }
      }
    }

    return basePriceCents * size;
  }

  getTierValue(source, size) {
    if (!source) return '';
    const tier = source
      .split(',')
      .map((entry) => entry.split(':').map((part) => part.trim()))
      .find(([tierSize]) => Number(tierSize) === Number(size));
    return tier?.[1] || '';
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('pack-selection')) {
  customElements.define('pack-selection', PackSelection);
}

async function handlePackGroupRemove(event) {
  const button = event.target.closest('[data-pack-remove-group]');
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  const packId = button.dataset.packRemoveGroup;
  if (!packId) return;

  button.disabled = true;

  const getCartSectionIds = () => {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component && component.dataset.sectionId) ids.push(component.dataset.sectionId);
    });
    return [...new Set(ids)];
  };

  const sections = getCartSectionIds();

  try {
    const cartResponse = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!cartResponse.ok) {
      throw new Error('Failed to fetch cart');
    }

    const cart = await cartResponse.json();
    const updates = {};

    cart.items.forEach((item) => {
      if (
        item.properties &&
        item.properties['_Pack Feature'] === 'Pack Selection' &&
        item.properties['_Pack ID'] === packId
      ) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      button.disabled = false;
      return;
    }

    const updateBody = { updates };
    if (sections.length) {
      updateBody.sections = sections.join(',');
      updateBody.sections_url = window.location.pathname;
    }

    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(updateBody),
    });

    if (!updateResponse.ok) {
      throw new Error('Failed to remove pack');
    }

    const updatedCart = await updateResponse.json();

    if (typeof CartAddEvent !== 'undefined' && updatedCart.sections) {
      document.dispatchEvent(
        new CartAddEvent(updatedCart, 'pack-selection', {
          source: 'pack-selection',
          itemCount: 0,
          sections: updatedCart.sections,
        })
      );
    } else {
      window.location.reload();
    }
  } catch (error) {
    console.error('Unable to remove pack:', error);
    button.disabled = false;
    window.location.reload();
  }
}

function initPackGroupRemove() {
  if (!window.__packGroupRemoveInitialized) {
    window.__packGroupRemoveInitialized = true;
    document.addEventListener('click', handlePackGroupRemove);
  }
}

async function updateCartSubtotalVisual() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const cart = await res.json();
    if (typeof cart.total_price !== 'number') return;

    const formattedTotal = `$${(cart.total_price / 100).toFixed(2)}`;

    const subtotalElements = document.querySelectorAll(
      '.cart__total-value, .totals__total-value, [ref="cartTotal"], [data-cart-total], .cart__subtotal-value, .cart-drawer__footer .totals__subtotal-value'
    );
    subtotalElements.forEach((el) => {
      const originalText = el.textContent || '';
      if (originalText.includes('USD')) {
        el.textContent = `${formattedTotal} USD`;
      } else if (originalText.includes('CAD')) {
        el.textContent = `${formattedTotal} CAD`;
      } else if (originalText.includes('AUD')) {
        el.textContent = `${formattedTotal} AUD`;
      } else if (originalText.includes('GBP')) {
        el.textContent = `£${(cart.total_price / 100).toFixed(2)} GBP`;
      } else if (originalText.includes('EUR')) {
        el.textContent = `€${(cart.total_price / 100).toFixed(2)} EUR`;
      } else {
        el.textContent = formattedTotal;
      }
    });
  } catch (e) {
    console.error('Error updating cart subtotal visual:', e);
  }
}

if (typeof window !== 'undefined') {
  initPackGroupRemove();
  const observeCart = () => {
    updateCartSubtotalVisual();
    setTimeout(updateCartSubtotalVisual, 200);
    setTimeout(updateCartSubtotalVisual, 500);
    setTimeout(updateCartSubtotalVisual, 1000);

    const observer = new MutationObserver(() => {
      observer.disconnect();
      updateCartSubtotalVisual();
      const cartWrapper = document.querySelector('.cart-items__wrapper') || document.body;
      observer.observe(cartWrapper, { childList: true, subtree: true });
    });

    const cartWrapper = document.querySelector('.cart-items__wrapper') || document.body;
    observer.observe(cartWrapper, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCart);
  } else {
    observeCart();
  }

  // Intercept global Fetch requests to trigger visual subtotal recalculations.
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const url = args[0];
    const options = args[1];

    if (typeof url === 'string' && options && options.method === 'POST') {
      if (
        url.includes('/cart/change') ||
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear') ||
        url.includes('/cart.js') ||
        url.includes('sections=')
      ) {
        setTimeout(updateCartSubtotalVisual, 100);
        setTimeout(updateCartSubtotalVisual, 300);
        setTimeout(updateCartSubtotalVisual, 600);
        setTimeout(updateCartSubtotalVisual, 1000);
      }
    }
    return originalFetch(...args);
  };
}